/**
 * Centralized marker color resolution for all map contexts.
 *
 * Provides three resolver functions covering different data shapes:
 *
 * 1. resolveMarkerColors(icon, properties)
 *    For bulk-action GeoJSON features (StopMarker DOM + BulkDeckMarkerLayer WebGL).
 *
 * 2. resolveRouteStopColors(stopData)
 *    For route-manager supercluster stop data (DeckMarkerRenderer WebGL).
 *
 * 3. resolveRouteClusterColors(routeId, routeLayers, theme)
 *    For route-manager cluster data (DeckMarkerRenderer WebGL).
 *
 * All functions share the same theme constants so colour choices stay
 * consistent across WebGL and DOM renderers.
 */
import { common, orange } from '@mui/material/colors'
import { get } from 'lodash'

import { hexToRgb } from 'components/common/map/webgl/generators/colorUtils'
import { WORK_ORDER_STATUS_DISPLAY } from 'settings/constants/service'
import { PRIMARY, COMMON, MARKER, UNASSIGNED } from 'theme/colors'
import { stopStatus } from 'utils/workOrderStatus'

export const pickContrastText = bgColor => {
  const [r, g, b] = hexToRgb(bgColor)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? COMMON.black : COMMON.white
}

/**
 * Colors for a stop that is pending assignment or under active resequencing.
 * When not active: white inner, UNASSIGNED border (matches the unassigned-route
 * treatment elsewhere). When active: invert — UNASSIGNED inner, white border.
 *
 * @param {Object} [opts]
 * @param {boolean} [opts.isActive=false] - whether the pending stop is currently selected/dragged
 * @returns {{ bgColor: string, borderColor: string, textColor: string }}
 */
export const resolvePendingMarkerColors = ({ isActive = false } = {}) => {
  if (isActive) {
    return { bgColor: UNASSIGNED.main, borderColor: COMMON.white, textColor: COMMON.black }
  }
  return { bgColor: COMMON.white, borderColor: UNASSIGNED.main, textColor: COMMON.black }
}

// ---------------------------------------------------------------------------
// Shared GeoJSON feature parsing
// ---------------------------------------------------------------------------

/**
 * Parses a GeoJSON stop feature into a flat object used by both the WebGL
 * (BulkDeckMarkerLayer) and DOM (StopMarker) renderers.
 *
 * @param {Object} feature - GeoJSON feature with geometry.coordinates and properties
 * @returns {{ lng: number, lat: number, properties: Object, icon: string, position: string, id: * } | null}
 */
export const parseStopFeature = feature => {
  const coords = get(feature, 'geometry.coordinates', [])
  const lng = parseFloat(coords[0])
  const lat = parseFloat(coords[1])
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null

  const properties = get(feature, 'properties', {})
  const icon = get(properties, 'icon', 'monochrome-marker')
  const rawPosition = get(properties, 'position')
  const label = rawPosition != null && rawPosition !== '' ? String(rawPosition) : ''
  const id = get(properties, 'id')

  return { lng, lat, properties, icon, label, id }
}

// ---------------------------------------------------------------------------
// Bulk-action markers (GeoJSON feature properties)
// ---------------------------------------------------------------------------

/**
 * Resolves marker colors based on icon type and GeoJSON feature properties.
 *
 * @param {string} icon - marker icon type (highlighted-marker, to-be-assigned-marker, etc.)
 * @param {Object} properties - GeoJSON feature properties
 * @returns {{ bgColor: string, borderColor: string, textColor: string }}
 */
export const resolveMarkerColors = (icon, properties) => {
  // _isStagedForMove swaps only the border to PRIMARY.main; inner colour
  // stays the stop's own work-order / route colour.
  if (get(properties, '_isStagedForMove') === true) {
    const woColor = get(properties, 'work-order-color')
    const bgColor = woColor || get(properties, 'color') || MARKER.defaultBg
    return { bgColor, borderColor: PRIMARY.main, textColor: pickContrastText(bgColor) }
  }
  switch (icon) {
    case 'highlighted-marker':
    case 'highlighted-container':
      return { bgColor: COMMON.white, borderColor: PRIMARY.main, textColor: COMMON.black }
    case 'to-be-assigned-marker': {
      // Active = the pending stop the user is currently resequencing/dragging.
      // Inactive (default) uses the unassigned-color treatment so pending stops
      // match the rest of the map's unassigned styling.
      const isActive = Boolean(get(properties, 'is-active'))
      return resolvePendingMarkerColors({ isActive })
    }
    case 'unassigned-marker': {
      // Unassigned stops carry their own identity colour so the user can spot
      // them at a glance regardless of which route they came from. Inner fill
      // stays the work-order-status colour; border switches to UNASSIGNED.main
      // (orange) instead of inheriting the route's colour.
      const woColor = get(properties, 'work-order-color')
      const bgColor = woColor || get(properties, 'color') || MARKER.defaultBg
      const textColor = pickContrastText(bgColor)
      return { bgColor, borderColor: UNASSIGNED.main, textColor }
    }
    default: {
      // Accept white as a legitimate work-order-color (master-route stops without a status).
      const woColor = get(properties, 'work-order-color')
      const bgColor = woColor || get(properties, 'color') || MARKER.defaultBg
      const textColor = pickContrastText(bgColor)
      return { bgColor, borderColor: get(properties, 'color') || MARKER.defaultBg, textColor }
    }
  }
}

// ---------------------------------------------------------------------------
// Route-manager stop markers (supercluster stop data)
// ---------------------------------------------------------------------------

/**
 * Resolves marker colors for a route-manager stop from supercluster properties.
 *
 * @param {Object} stopData - supercluster leaf point properties
 * @param {string} stopData.routeId - route ID or 'unassigned'
 * @param {Object} [stopData.colors] - pre-computed status / route color groups
 * @param {Object} [stopData.colors.status] - { bg, text }
 * @param {Object} [stopData.colors.route]  - { bg }
 * @returns {{ innerColor: string, outerColor: string, textColor: string }}
 */
export const resolveRouteStopColors = stopData => {
  const isAssigned = stopData.routeId !== 'unassigned'
  if (isAssigned) {
    return {
      innerColor: stopData.colors?.status?.bg ?? COMMON.black,
      outerColor: stopData.colors?.route?.bg ?? COMMON.white,
      textColor: stopData.colors?.status?.text ?? COMMON.white,
    }
  }
  const statusBg = stopData.colors?.status?.bg ?? UNASSIGNED.main
  return { innerColor: COMMON.white, outerColor: statusBg, textColor: COMMON.black }
}

/**
 * Resolves colours for a configured-service route stop based on its work
 * order status and pending state. Sibling to `resolveRouteStopColors` above —
 * the route-manager variant uses pre-computed colour groups from
 * supercluster; this variant resolves on the fly from work-order status +
 * the selected route's colour.
 *
 * Field naming (`bgColor`/`borderColor` vs `innerColor`/`outerColor`) is
 * preserved from the original `configured-service/utils/` location for
 * backwards compatibility with the container view + WebGLSequenceMarkerLayer.
 *
 * @param {Object} stop - stop object with workOrderStatus and isPending
 * @param {Object} selectedRoute - route object with color property
 * @param {Object} theme - MUI theme (for palette.getContrastText / palette.text.primary)
 * @returns {{ bgColor: string, textColor: string, borderColor: string }}
 */
export const resolveStopMarkerColors = (stop, selectedRoute, theme) => {
  const { isPending } = stop
  const statusColors = WORK_ORDER_STATUS_DISPLAY[stopStatus(stop)]
  const workOrderColor = statusColors?.borderColor || common.black

  return {
    bgColor: isPending ? common.white : workOrderColor,
    textColor: isPending ? theme.palette.text.primary : theme.palette.getContrastText(workOrderColor),
    borderColor: isPending ? orange[400] : selectedRoute?.color || statusColors?.borderColor || common.white,
  }
}

// ---------------------------------------------------------------------------
// Route-manager cluster markers
// ---------------------------------------------------------------------------

/**
 * Resolves colours for a per-route cluster marker in the route manager map.
 *
 * @param {string} routeId - route ID or 'unassigned'
 * @param {Object} routeLayers - routeId → { routeColor, … } lookup
 * @param {Object} theme - MUI theme (for fallback primary color)
 * @returns {{ bgColor: string, borderColor: string, textColor: string }}
 */
export const resolveRouteClusterColors = (routeId, routeLayers, theme) => {
  if (routeId === 'unassigned') {
    return { bgColor: COMMON.white, borderColor: UNASSIGNED.main, textColor: COMMON.black }
  }
  const routeColor = routeLayers[routeId]?.routeColor
  const safeBgColor =
    routeColor === 'black' || routeColor === 'Black' ? theme.palette.common.black : routeColor || theme.palette.primary.main
  return {
    bgColor: safeBgColor,
    borderColor: COMMON.white,
    textColor: pickContrastText(safeBgColor),
  }
}
